/* eslint-disable react/jsx-child-element-spacing, react/jsx-max-props-per-line, react/jsx-one-expression-per-line */
import { useCallback, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSetPageTitle } from 'src/hooks/useSetPageTitle';
import { Button } from 'src/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { useListBillingImportsQuery } from './pipeline.endpoints';
import type { BillingImportSource, BillingImportStatus } from './types/pipeline.types';
import { BillingPipelineShell } from './billing-pipeline-shell';
import { ImportDetailSheet } from './components/import-detail-sheet';
import type { BillingImportDTO } from './types/pipeline.types';

const SOURCES: BillingImportSource[] = [
  'dsp_api_1',
  'dsp_api_2',
  'bigquery',
  'source_4',
  'source_5',
  'source_6',
];
const STATUSES: BillingImportStatus[] = ['pending', 'running', 'complete', 'failed'];

function usePipelineImportQueryParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const source = (searchParams.get('source') as BillingImportSource | null) || undefined;
  const status = (searchParams.get('status') as BillingImportStatus | null) || undefined;
  const startDate = searchParams.get('start_date') || undefined;
  const endDate = searchParams.get('end_date') || undefined;
  const page = Math.max(1, Number(searchParams.get('page') || '1') || 1);
  const pageSize = Math.min(100, Math.max(1, Number(searchParams.get('page_size') || '25') || 25));

  const setFilters = useCallback(
    (next: {
      source?: string;
      status?: string;
      start_date?: string;
      end_date?: string;
      page?: number;
      page_size?: number;
    }) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          const assign = (key: string, val: string | number | undefined) => {
            if (val === undefined || val === '') {
              p.delete(key);
            } else {
              p.set(key, String(val));
            }
          };
          if ('source' in next) {
            assign('source', next.source);
          }
          if ('status' in next) {
            assign('status', next.status);
          }
          if ('start_date' in next) {
            assign('start_date', next.start_date);
          }
          if ('end_date' in next) {
            assign('end_date', next.end_date);
          }
          if (next.page != null) {
            assign('page', next.page);
          }
          if (next.page_size != null) {
            assign('page_size', next.page_size);
          }
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return { source, status, startDate, endDate, page, pageSize, setFilters };
}

function recordCount(row: BillingImportDTO): number {
  return row.raw_data?.normalized_records?.length ?? 0;
}

export default function PipelineImportsPage() {
  useSetPageTitle('Billing — Pipeline imports');
  const { source, status, startDate, endDate, page, pageSize, setFilters } = usePipelineImportQueryParams();
  const { data, isLoading, isError } = useListBillingImportsQuery({
    source,
    status,
    start_date: startDate,
    end_date: endDate,
    page,
    page_size: pageSize,
  });

  const [detailRow, setDetailRow] = useState<BillingImportDTO | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const rows = data?.results ?? [];
  const total = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const emptyMessage = useMemo(() => {
    if (isLoading) {
      return 'Loading…';
    }
    if (isError) {
      return 'Failed to load imports.';
    }
    return 'No import runs match the filters.';
  }, [isLoading, isError]);

  return (
    <>
      <BillingPipelineShell>
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-xs text-gray-600">
              Source
              <select
                className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm"
                value={source ?? ''}
                onChange={(e) =>
                  setFilters({ source: e.target.value || undefined, page: 1 })
                }
              >
                <option value="">All</option>
                {SOURCES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-gray-600">
              Status
              <select
                className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm"
                value={status ?? ''}
                onChange={(e) =>
                  setFilters({ status: e.target.value || undefined, page: 1 })
                }
              >
                <option value="">All</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-gray-600">
              Start date
              <input
                type="date"
                className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm"
                value={startDate ?? ''}
                onChange={(e) => setFilters({ start_date: e.target.value || undefined, page: 1 })}
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-gray-600">
              End date
              <input
                type="date"
                className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm"
                value={endDate ?? ''}
                onChange={(e) => setFilters({ end_date: e.target.value || undefined, page: 1 })}
              />
            </label>
          </div>

          <div className="rounded-md border border-gray-200 bg-white overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date range</TableHead>
                  <TableHead className="text-right">Records</TableHead>
                  <TableHead>Error</TableHead>
                  <TableHead>Completed</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-gray-500 py-8">
                      {emptyMessage}
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-mono text-xs">{row.id}</TableCell>
                      <TableCell>{row.source}</TableCell>
                      <TableCell>{row.status}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">
                        {row.raw_data?.start_date ?? '—'} → {row.raw_data?.end_date ?? '—'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{recordCount(row)}</TableCell>
                      <TableCell className="max-w-[200px] truncate text-xs text-red-600" title={row.error_message}>
                        {row.error_message || '—'}
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{row.completed_at ?? '—'}</TableCell>
                      <TableCell className="text-right space-x-2 whitespace-nowrap">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setDetailRow(row);
                            setSheetOpen(true);
                          }}
                        >
                          Detail
                        </Button>
                        <Button type="button" variant="outline" size="sm" asChild>
                          <Link to={`/billing/pipeline/imports/${row.id}/line-items`}>Line items</Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              Page {page} of {totalPages} ({total} total)
            </span>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setFilters({ page: page - 1 })}
              >
                Previous
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setFilters({ page: page + 1 })}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </BillingPipelineShell>

      <ImportDetailSheet
        open={sheetOpen}
        onOpenChange={(open) => {
          setSheetOpen(open);
          if (!open) {
            setDetailRow(null);
          }
        }}
        row={detailRow}
      />
    </>
  );
}
